import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { sitesApi } from '@/api/client';
import { parseApiError } from '@/lib/apiError';

/**
 * Delete Site: type the site's name to confirm. A refusal shows the server's
 * message (`Site not found`, a role refusal, …) rather than the raw
 * `API error 403: {…}` it used to (JSS#959).
 */
export function DeleteSiteDialog({
  siteId,
  siteName,
  onClose,
  onDeleted,
}: {
  siteId: string;
  siteName: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirmName, setConfirmName] = useState('');
  const deleteSite = useMutation({
    mutationFn: () => sitesApi.delete(siteId),
    onSuccess: onDeleted,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h3 className="text-lg font-semibold text-status-error">Delete Site</h3>
          <button onClick={onClose} aria-label="Close" className="text-silver-muted hover:text-silver-bright">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-silver-muted">
            This will permanently delete <span className="text-silver-bright font-semibold">{siteName}</span>, including all nodes, releases, and WireGuard tunnels. This action cannot be undone.
          </p>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">
              Type <span className="text-silver-bright font-semibold">{siteName}</span> to confirm
            </label>
            <input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              className="w-full bg-surface-deep border border-status-error/30 rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-status-error/60"
              placeholder={siteName}
              autoFocus
            />
          </div>
          {deleteSite.isError && (
            <p role="alert" className="text-xs text-status-error">
              {parseApiError(deleteSite.error).message}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteSite.mutate()}
              disabled={confirmName !== siteName || deleteSite.isPending}
              className="px-4 py-2 bg-status-error text-white text-sm font-medium rounded-lg hover:bg-status-error/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {deleteSite.isPending ? 'Deleting...' : 'Delete Site'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
